export type DurationInputUnit =
  | "ms"
  | "millisecond"
  | "milliseconds"
  | "s"
  | "second"
  | "seconds"
  | "m"
  | "minute"
  | "minutes"
  | "h"
  | "hour"
  | "hours"
  | "d"
  | "day"
  | "days"
  | "years"
  | "year"
  | "y";

export type NormalizedUnit =
  | "milliseconds"
  | "seconds"
  | "minutes"
  | "hours"
  | "days"
  | "years";

const unitToMs: Record<NormalizedUnit, number> = {
  milliseconds: 1,
  seconds: 1_000,
  minutes: 60_000,
  hours: 3_600_000,
  days: 86_400_000,
  years: 31_557_600_000,
};

const aliasMap: Record<DurationInputUnit, NormalizedUnit> = {
  ms: "milliseconds",
  millisecond: "milliseconds",
  milliseconds: "milliseconds",
  s: "seconds",
  second: "seconds",
  seconds: "seconds",
  m: "minutes",
  minute: "minutes",
  minutes: "minutes",
  h: "hours",
  hour: "hours",
  hours: "hours",
  d: "days",
  day: "days",
  days: "days",
  years: "years",
  year: "years",
  y: "years",
};

export function duration(value: number, unit: DurationInputUnit) {
  const ms = value * unitToMs[aliasMap[unit]];

  const methods = {
    /**
     * Converts the current duration to another unit.
     */
    as: (targetUnit: DurationInputUnit): number => {
      const target = aliasMap[targetUnit];
      return ms / unitToMs[target];
    },

    /**
     * Adds another duration and returns a new immutable instance.
     */
    add: (addValue: number, addUnit: DurationInputUnit) => {
      return duration(
        ms + duration(addValue, addUnit).milliseconds,
        "milliseconds",
      );
    },

    /**
     * Subtracts another duration and returns a new immutable instance.
     */
    subtract: (subValue: number, subUnit: DurationInputUnit) => {
      return duration(
        ms - duration(subValue, subUnit).milliseconds,
        "milliseconds",
      );
    },

    /**
     * Returns the raw value in milliseconds.
     */
    valueOf: () => ms,

    /**
     * Converts the duration to string in milliseconds.
     */
    toString: () => `${ms}ms`,

    /**
     * Clones the current duration.
     */
    clone: () => duration(ms, "milliseconds"),

    /**
     * Converts duration to primitive value
     */
    [Symbol.toPrimitive]: (hint: string) => {
      if (hint === "string") return `${ms}ms`;
      return ms;
    },
  };

  type DurationInstance = typeof methods & {
    [K in DurationInputUnit]: number;
  };

  const proxy = new Proxy(
    {},
    {
      get(_: object, prop: string): any {
        if (prop in methods) return methods[prop as keyof typeof methods];

        if (prop in aliasMap) {
          const normalized = aliasMap[prop as DurationInputUnit];
          return ms / unitToMs[normalized];
        }

        throw new Error(`Invalid property access: ${prop}`);
      },
    },
  ) as DurationInstance;

  return proxy;
}

export function milis(value: number, unit: DurationInputUnit) {
  return duration(value, unit).milliseconds;
}
